import { useEffect, useRef, useState } from "react";
import { PiMagnifyingGlassBold } from "react-icons/pi";
import { Input } from "rizzui";
import { useLocation, useNavigation } from "react-router-dom";
import { FILTER_LABEL } from "./toolbar";

/**
 * The toolbar search box over a SERVER-side filter (a `?q=`-style param the loader refetches on).
 * Same rizzui `Input` configuration as vendor order-list/filters.tsx's search box; the only
 * difference is timing: `onSearch` (the navigation that refetches) runs once typing pauses, not on
 * every keystroke, so a word costs one request and one history entry instead of one per letter.
 * The box follows `value` when the URL changes underneath it (Clear, Back, a link) — and such an
 * outside navigation also cancels a search still waiting on its pause, which would otherwise fire
 * afterwards and put the cleared `?q=` back. The box's own navigation is told apart by the value it
 * sent, so landing on it neither cancels nor overwrites what was typed while it loaded.
 */
export function SearchInput({
  value,
  placeholder,
  onSearch,
}: {
  value: string | undefined;
  placeholder: string;
  onSearch: (next: string | undefined) => void;
}) {
  const [text, setText] = useState(value ?? "");
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const sent = useRef<{ value: string | undefined } | null>(null);
  const { key } = useLocation();
  const navigation = useNavigation();
  const starting = useRef(false);

  // A navigation this box did not start (Clear, a status link, Back) cancels the pending search the
  // moment it begins — the location only changes once its loader finishes, which is too late.
  useEffect(() => {
    if (navigation.state === "idle") return;
    if (starting.current) starting.current = false;
    else clearTimeout(timer.current);
  }, [navigation.state, navigation.location?.key]);

  useEffect(() => {
    if (sent.current && sent.current.value === value) {
      sent.current = null;
      return;
    }
    sent.current = null;
    clearTimeout(timer.current);
    setText(value ?? "");
  }, [value, key]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const search = (next: string | undefined) => {
    sent.current = { value: next };
    starting.current = true;
    onSearch(next);
  };

  return (
    <Input
      type="search"
      label="Search"
      labelClassName={FILTER_LABEL}
      placeholder={placeholder}
      value={text}
      onClear={() => {
        clearTimeout(timer.current);
        setText("");
        search(undefined);
      }}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => search(next.trim() || undefined), 350);
      }}
      className="w-full @5xl:w-64"
      clearable
      prefix={<PiMagnifyingGlassBold className="size-4" />}
    />
  );
}
